/**
 * Component for displaying tool call information in dialogs
 */

import { CheckCircle2, XCircle } from 'lucide-react';
import React from 'react';
import { getToolIcon, getToolDisplayName } from './ToolIcons';
import { toDisplay } from '../utils/formatting';

export type ToolCallDetail = {
  tool_name?: string;
  args?: Record<string, unknown>;
  output_length?: number;
  output_preview?: string;
  success?: boolean;
  tool_call_id?: string;
};

export type ToolCallDisplayProps = {
  toolCalls: ToolCallDetail[];
};

export const ToolCallDisplay: React.FC<ToolCallDisplayProps> = ({ toolCalls }) => {
  if (!toolCalls || toolCalls.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: '#f9fafb',
        borderRadius: 8,
        border: '1px solid #e5e7eb',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#374151' }}>Tools Used</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {toolCalls.map((tool, idx) => {
          const ToolIcon = getToolIcon(tool.tool_name || '');
          const displayName = getToolDisplayName(tool.tool_name || 'Unknown');
          const isSuccess = tool.success !== false; // Default to true if undefined

          return (
            <div
              key={idx}
              style={{
                padding: 10,
                background: '#ffffff',
                borderRadius: 6,
                border: '1px solid #e5e7eb',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <ToolIcon size={16} />
                <span style={{ fontWeight: 600, fontSize: 12, flex: 1 }}>{displayName}</span>
                {isSuccess ? <CheckCircle2 size={14} color="#10b981" /> : <XCircle size={14} color="#ef4444" />}
              </div>

              {/* Display arguments */}
              {tool.args && Object.keys(tool.args).length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  {Object.entries(tool.args).map(([key, value]) => {
                    // Skip raw if we have parsed args
                    if (key === 'raw' && Object.keys(tool.args!).length > 1) return null;

                    // Special handling for web_search query
                    if (tool.tool_name === 'web_search' && key === 'query') {
                      return (
                        <div key={key} style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>
                          <span style={{ fontWeight: 500 }}>Search Query:</span>{' '}
                          <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#1f2937' }}>
                            "{toDisplay(value, { truncate: 150 })}"
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div key={key} style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>
                        <span style={{ fontWeight: 500 }}>{key}:</span>{' '}
                        <span style={{ fontFamily: 'monospace', fontSize: 10 }}>
                          {toDisplay(value, { truncate: 150 })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Display output info */}
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                {tool.output_length != null && (
                  <span>
                    Result: {tool.output_length} characters
                    {tool.output_preview && (
                      <span
                        title={tool.output_preview}
                        style={{ marginLeft: 4, cursor: 'help', textDecoration: 'underline dotted' }}
                      >
                        (preview)
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
